import { useEffect, useRef, useState, forwardRef } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import { LOGO_URL } from '@/data/mockData';

// Single hero video on the landing. Was previously a 3-video showcase stack;
// the product decision (Feb 2026) is to spotlight ONE flagship piece with the
// long-form story copy below it.
const DEFAULT_STACK = [
  {
    id: 'hero',
    credits: 6,
    messageTitle: '6 Credits — See What Gets Built',
    messageSub:
      "Every video starts with your vision. You share your idea through our chat — from there, our team creates multiple visual concepts, selects the best scenes, animates the characters, writes the script and composes the music. Everything is built around your goal — whether it's a social media reel, a product launch or a brand campaign. Each piece is crafted by real people using AI as a tool, delivered ready to use. This is what 6 credits looks like.",
    src: '/hero/hero.mp4',
    poster: '/hero/hero-poster.jpg',
    accent: 'from-emerald-200/55 via-emerald-50/30 to-white/10',
  },
];

const ShowcaseStack = forwardRef(function ShowcaseStack(
  { items = DEFAULT_STACK, scrollRoot = null },
  ref,
) {
  const [activeId, setActiveId] = useState(items[0]?.id);
  const [unmutedId, setUnmutedId] = useState(null);
  const sectionRefs = useRef({});
  const videoRefs = useRef({});
  const cardRefs = useRef({});

  // Observe each section against the scroll container — whichever section's
  // VISUAL CENTER is closest to the viewport center drives autoplay. On the
  // same pass we apply a tiny translateY "drag" to each card while it's
  // within ±20% of the viewport center, so videos feel like they have a
  // little weight as they cross the center WITHOUT ever stopping. Scroll
  // always keeps responding to the finger/wheel — this is purely visual
  // lag, no scroll lock, no snap, no sticky freeze.
  useEffect(() => {
    const scroller = scrollRoot?.current ?? document.documentElement;
    if (!scroller) return;

    const rootElForRect = () => (scrollRoot?.current ?? document.documentElement);
    const MAX_DRAG_PX = 26; // peak lag at the exact center

    const recompute = () => {
      const rootRect = rootElForRect().getBoundingClientRect();
      const rootCenter = rootRect.top + rootRect.height / 2;
      const zone = rootRect.height * 0.2; // ±20% of viewport height
      let bestId = null;
      let bestDist = Infinity;
      for (const it of items) {
        const el = sectionRefs.current[it.id];
        const card = cardRefs.current[it.id];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        // Outside viewport — make sure there's no residual transform left
        if (r.bottom < rootRect.top || r.top > rootRect.bottom) {
          if (card) card.style.transform = '';
          continue;
        }
        const sectionCenter = r.top + r.height / 2;
        const dist = Math.abs(sectionCenter - rootCenter);
        if (dist < bestDist) { bestDist = dist; bestId = it.id; }

        // Friction curve: peaks at center, smoothly decays to 0 at ±zone.
        if (card) {
          if (dist < zone) {
            const n = dist / zone; // 0 at center, 1 at zone edge
            const shift = MAX_DRAG_PX * (1 - n * n);
            card.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
          } else {
            card.style.transform = '';
          }
        }
      }
      if (bestId) setActiveId((prev) => (prev === bestId ? prev : bestId));
    };

    // Throttle via rAF — fires at most once per frame while scrolling.
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; recompute(); });
    };

    recompute();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRoot, items.length]);

  // Play/pause + mute mirror activeId / unmutedId. Idempotent side effects.
  useEffect(() => {
    items.forEach((it) => {
      const v = videoRefs.current[it.id];
      if (!v) return;
      if (it.id === activeId) {
        v.muted = unmutedId !== it.id;
        v.play?.().catch(() => { /* autoplay rejected — harmless */ });
      } else {
        try { v.pause?.(); } catch { /* noop */ }
        v.muted = true;
      }
    });
  }, [activeId, unmutedId, items]);

  // Only the currently-active video can ever be unmuted.
  useEffect(() => {
    if (unmutedId && unmutedId !== activeId) setUnmutedId(null);
  }, [activeId, unmutedId]);

  return (
    <section
      ref={ref}
      data-testid="showcase-stack"
      className="w-full flex flex-col gap-9 pb-[44px]"
    >
      {items.map((it, idx) => {
        const isActive = it.id === activeId;
        const isUnmuted = unmutedId === it.id;
        return (
          <div
            key={it.id}
            ref={(el) => { sectionRefs.current[it.id] = el; }}
            data-id={it.id}
            data-testid={`showcase-section-${it.id}`}
            className="relative flex justify-center px-4"
          >
            {/* Card holds both the video AND its message together. No sticky
                pin — the card flows with the scroll naturally so videos never
                feel "parked" at the center. Natural browser inertia + the
                cross-fade on the active card give the soft rest feel. */}
            <div
              ref={(el) => { cardRefs.current[it.id] = el; }}
              data-testid={`showcase-card-${it.id}`}
              className={`mx-auto w-full max-w-[400px] flex flex-col items-stretch gap-2.5 transition-opacity duration-[200ms] ease-[cubic-bezier(0.25,0.8,0.25,1)] ${
                isActive
                  ? 'opacity-100'
                  : 'opacity-80'
              }`}
              style={{ willChange: 'transform, opacity' }}
            >
              {/* Caption — sits ABOVE the video, inline like the paragraphs
                  below ("No es contenido automático…"). No white card, no
                  shadow — just avatar + two lines. Fades in when its video
                  becomes active so the message always matches the focused
                  media. */}
              <div
                data-testid={`showcase-message-${it.id}`}
                className="flex items-start gap-2.5 px-1"
              >
                <span className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                  <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                </span>
                <div className="flex-1 min-w-0 leading-snug">
                  <p className="text-[13px] font-semibold text-gray-800 tracking-tight">
                    {it.messageTitle}
                  </p>
                  <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
                    {it.messageSub}
                  </p>
                </div>
              </div>

              {/* Media bubble — clean, no floating text on top. 9:16. */}
              <div className="relative w-full aspect-[9/16] max-h-[68vh] overflow-hidden rounded-[26px] border border-white/40 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.3)] backdrop-blur-md">
                {/* Gradient placeholder — real <video> takes over when src set */}
                <div className={`absolute inset-0 bg-gradient-to-br ${it.accent} bg-white/40`} />
                <div
                  className="absolute inset-0 mix-blend-overlay opacity-50 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(120% 70% at 30% 20%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 60%), radial-gradient(80% 50% at 70% 90%, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 70%)',
                  }}
                />

                {it.src ? (
                  <video
                    ref={(el) => { videoRefs.current[it.id] = el; }}
                    data-testid={`showcase-video-${it.id}`}
                    className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                    src={it.src}
                    poster={it.poster || undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    // `preload="auto"` is critical on mobile — `metadata` was
                    // forcing iOS Safari/Chrome to wait until first paint
                    // before fetching frames, which made the hero feel
                    // "stuck" on slow connections. The file is now 5.7MB
                    // H.264 + faststart, so eager preload pays off.
                    preload="auto"
                    // Help Safari schedule decode early.
                    // eslint-disable-next-line react/no-unknown-property
                    disableRemotePlayback
                    onClick={(e) => {
                      // Tap the video itself to toggle audio. We deliberately
                      // do NOT touch v.currentTime — toggling `muted` keeps
                      // playback uninterrupted (no restart).
                      e.stopPropagation();
                      setUnmutedId((prev) => (prev === it.id ? null : it.id));
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400/60">
                    <Play className="w-12 h-12" />
                  </div>
                )}

                {/* Soft bottom fade so the audio pill stays legible on any video. */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

                {/* Audio toggle — bottom-right, active video only. */}
                {isActive && (
                  <button
                    type="button"
                    data-testid={`showcase-audio-${it.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnmutedId((prev) => (prev === it.id ? null : it.id));
                    }}
                    className="absolute bottom-3 right-3 p-2 rounded-full bg-black/55 text-white hover:bg-black/75 transition-colors backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/60"
                    aria-label={isUnmuted ? 'Mute' : 'Unmute'}
                  >
                    {isUnmuted ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
});

export default ShowcaseStack;
